import {
    hasAutoDownloadedOperationId,
    hasDismissedOperationId,
    isWithinCompletedOperationRetention,
    operationIdsMatch,
} from "./autoDownloadedOperationIds.js";

function upsertCompletedOperation(initiated_operations, completed_operations, operation) {
    const initiatedIndex = initiated_operations.findIndex(
        (item) => operationIdsMatch(item.id, operation.id),
    );
    if (initiatedIndex !== -1) {
        initiated_operations.splice(initiatedIndex, 1);
    }

    const completedIndex = completed_operations.findIndex(
        (item) => operationIdsMatch(item.id, operation.id),
    );
    if (completedIndex !== -1) {
        completed_operations.splice(completedIndex, 1, operation);
    } else {
        completed_operations.push(operation);
    }
}

function removeInitiatedOperation(initiated_operations, id) {
    const initiatedIndex = initiated_operations.findIndex(
        (item) => operationIdsMatch(item.id, id),
    );
    if (initiatedIndex !== -1) {
        initiated_operations.splice(initiatedIndex, 1);
    }
}

function reportName(progress, existing) {
    return progress.report_name || existing?.name;
}

function operationTimestamps(progress, existing) {
    const now = new Date().toISOString();

    return {
        created_at: progress.created_at ?? existing?.created_at ?? now,
        updated_at: progress.updated_at ?? existing?.updated_at ?? now,
    };
}

function dropCompletedOperation(completed_operations, id) {
    const completedIndex = completed_operations.findIndex(
        (operation) => operationIdsMatch(operation.id, id),
    );
    if (completedIndex !== -1) {
        completed_operations.splice(completedIndex, 1);
    }
}

function shouldRetainCompletedOperation(
    progress,
    dismissed_operation_ids,
    now = Date.now(),
) {
    if (hasDismissedOperationId(dismissed_operation_ids, progress.id)) {
        return false;
    }

    const updatedAt =
        progress.updated_at ?? progress.created_at ?? null;

    return isWithinCompletedOperationRetention(updatedAt, now);
}

function pruneCompletedOperations(
    completed_operations,
    dismissed_operation_ids,
    now = Date.now(),
) {
    for (let index = completed_operations.length - 1; index >= 0; index -= 1) {
        const operation = completed_operations[index];

        if (
            !shouldRetainCompletedOperation(
                operation,
                dismissed_operation_ids,
                now,
            )
        ) {
            completed_operations.splice(index, 1);
        }
    }
}

/**
 * Apply reconnect / refresh catch-up for generate-report progresses.
 * Successful exports this session was already waiting on are returned as downloads.
 * Auto-downloaded exports stay in completed_operations for the retention window.
 * Completed exports discovered on a cold load stay queued for manual download.
 * Failed exports stay as completed entries (status error) and emit error notifications.
 *
 * @param {{
 *   initiated_operations?: Array<{id: number|string, name?: string}>,
 *   completed_operations?: Array<{id: number|string, name?: string, url?: string|null, status?: string}>,
 *   initiated?: Array<{id: number|string, report_name?: string|null}>,
 *   completed?: Array<{id: number|string, report_name?: string|null, url?: string|null}>,
 *   failed?: Array<{id: number|string, report_name?: string|null}>,
 *   cancelled?: Array<{id: number|string}>,
 *   auto_downloaded_operation_ids?: Array<number|string>,
 *   dismissed_operation_ids?: Array<number|string>,
 * }} input
 * @returns {{
 *   initiated_operations: Array<object>,
 *   completed_operations: Array<object>,
 *   downloads: Array<{id: number|string, name?: string}>,
 *   notifications: Array<{type: 'info'|'error', message: string}>,
 * }}
 */
export function applyGenerateReportProgressCatchUp({
    initiated_operations = [],
    completed_operations = [],
    initiated = [],
    completed = [],
    failed = [],
    cancelled = [],
    auto_downloaded_operation_ids = [],
    dismissed_operation_ids = [],
} = {}) {
    const now = Date.now();
    const next_initiated = [...initiated_operations];
    const next_completed = [...completed_operations];
    const notifications = [];
    const downloads = [];

    pruneCompletedOperations(
        next_completed,
        dismissed_operation_ids,
        now,
    );

    for (const progress of initiated ?? []) {
        const existing = next_initiated.find((operation) =>
            operationIdsMatch(operation.id, progress.id),
        );
        const already_completed = next_completed.some((operation) =>
            operationIdsMatch(operation.id, progress.id),
        );

        if (already_completed) {
            continue;
        }

        const name = reportName(progress, existing);

        if (!existing) {
            next_initiated.push({
                id: progress.id,
                name,
                can_cancel: Boolean(progress.can_cancel),
                ...operationTimestamps(progress),
            });
        } else {
            if (name) {
                existing.name = name;
            }
            existing.can_cancel = Boolean(
                existing.can_cancel || progress.can_cancel,
            );
            Object.assign(existing, operationTimestamps(progress, existing));
        }
    }

    for (const progress of completed ?? []) {
        if (!shouldRetainCompletedOperation(progress, dismissed_operation_ids, now)) {
            removeInitiatedOperation(next_initiated, progress.id);
            dropCompletedOperation(next_completed, progress.id);
            continue;
        }

        const existing = next_initiated.find((operation) =>
            operationIdsMatch(operation.id, progress.id),
        );

        if (existing) {
            if (
                !hasAutoDownloadedOperationId(
                    auto_downloaded_operation_ids,
                    progress.id,
                )
            ) {
                downloads.push({
                    id: progress.id,
                    name: reportName(progress, existing),
                });
            }

            removeInitiatedOperation(next_initiated, progress.id);
            upsertCompletedOperation(next_initiated, next_completed, {
                id: progress.id,
                url: progress.url ?? null,
                name: reportName(progress, existing),
                status: "completed",
                ...operationTimestamps(progress, existing),
            });
            continue;
        }

        upsertCompletedOperation(next_initiated, next_completed, {
            id: progress.id,
            url: progress.url ?? null,
            name: reportName(progress, existing),
            status: "completed",
            ...operationTimestamps(progress, existing),
        });
    }

    for (const progress of failed ?? []) {
        if (!shouldRetainCompletedOperation(progress, dismissed_operation_ids, now)) {
            removeInitiatedOperation(next_initiated, progress.id);
            dropCompletedOperation(next_completed, progress.id);
            continue;
        }

        const existing = next_initiated.find((operation) =>
            operationIdsMatch(operation.id, progress.id),
        );
        const already_recorded = next_completed.some((operation) =>
            operationIdsMatch(operation.id, progress.id),
        );

        if (existing && !already_recorded) {
            notifications.push({
                type: "error",
                message: `${reportName(progress, existing)} failed to generate`,
            });
        }

        upsertCompletedOperation(next_initiated, next_completed, {
            id: progress.id,
            url: null,
            name: reportName(progress, existing),
            status: "error",
            ...operationTimestamps(progress, existing),
        });
    }

    for (const progress of cancelled ?? []) {
        removeInitiatedOperation(next_initiated, progress.id);
    }

    return {
        initiated_operations: next_initiated,
        completed_operations: next_completed,
        downloads,
        notifications,
    };
}
